import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { fuseAnimations } from "@fuse/animations";
import { FuseConfigService } from "@fuse/services/config.service";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { environment } from "environments/environment";
import { locale as catalan } from "../i18n/ca";
import { locale as english } from "../i18n/en";
import { locale as spanish } from "../i18n/es";
import { AuthService } from "app/services/api/auth.service";

type TurnstileApi = {
    render: (container: HTMLElement, options: Record<string, unknown>) => string;
    reset: (widgetId?: string) => void;
    remove?: (widgetId?: string) => void;
};

@Component({
    selector: "forgot-password",
    templateUrl: "./forgot-password.component.html",
    styleUrls: ["./forgot-password.component.scss"],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
    standalone: false,
})
export class ForgotPasswordComponent implements OnInit, AfterViewInit, OnDestroy {
    private static turnstileScriptPromise: Promise<void>;
    private widgetId: string = null;

    @ViewChild("turnstileContainer")
    turnstileContainer: ElementRef<HTMLDivElement>;

    forgotPasswordForm: UntypedFormGroup;
    loading = false;
    error: string = null;
    success: string = null;
    captchaToken: string = null;
    readonly turnstileSiteKey = environment.turnstile?.siteKey || "";
    readonly turnstileEnabled = !!this.turnstileSiteKey;

    constructor(
        private fuseConfigService: FuseConfigService,
        private formBuilder: UntypedFormBuilder,
        private authService: AuthService,
        private router: Router,
        private translation: FuseTranslationLoaderService,
        private cdr: ChangeDetectorRef,
    ) {
        this.translation.loadTranslations(english, spanish, catalan);
        this.fuseConfigService.config = {
            layout: {
                navbar: { hidden: true },
                toolbar: { hidden: true },
                footer: { hidden: true },
                sidepanel: { hidden: true },
            },
        };
    }

    ngOnInit(): void {
        this.forgotPasswordForm = this.formBuilder.group({
            email: ["", [Validators.required, Validators.email]],
        });
    }

    async ngAfterViewInit(): Promise<void> {
        if (!this.turnstileEnabled) {
            return;
        }

        try {
            await this.loadTurnstileScript();
            const turnstile = this.getTurnstile();
            const container = this.turnstileContainer?.nativeElement;

            if (!turnstile || !container) {
                this.error = "login.passwordRecoveryCaptchaError.message";
                this.cdr.detectChanges();
                return;
            }

            this.widgetId = turnstile.render(container, {
                sitekey: this.turnstileSiteKey,
                action: "password_recovery",
                callback: (token: string) => {
                    this.captchaToken = token;
                    this.error = null;
                    this.cdr.detectChanges();
                },
                "expired-callback": () => {
                    this.captchaToken = null;
                    this.cdr.detectChanges();
                },
                "error-callback": () => {
                    this.captchaToken = null;
                    this.error = "login.passwordRecoveryCaptchaError.message";
                    this.cdr.detectChanges();
                },
            });
            this.cdr.detectChanges();
        } catch {
            this.error = "login.passwordRecoveryCaptchaError.message";
            this.cdr.detectChanges();
        }
    }

    ngOnDestroy(): void {
        const turnstile = this.getTurnstile();
        if (turnstile && this.widgetId && turnstile.remove) {
            turnstile.remove(this.widgetId);
        }
    }

    submit(): void {
        if (this.turnstileEnabled && !this.captchaToken) {
            this.error = "login.passwordRecoveryCaptchaRequired.message";
            return;
        }

        this.loading = true;
        this.error = null;
        this.success = null;

        this.authService.passwordRecovery(this.forgotPasswordForm.value.email, this.captchaToken).subscribe(
            () => {
                this.success = "login.passwordRecoveryConfirmation.message";
                this.loading = false;
                this.resetTurnstile();
                this.cdr.detectChanges();
            },
            (error) => {
                this.error = error?.status === 400 ? "login.passwordRecoveryCaptchaError.message" : "login.passwordRecoveryError.message";
                this.loading = false;
                this.resetTurnstile();
                this.cdr.detectChanges();
            },
        );
    }

    backToLogin(): void {
        this.router.navigate(["/login"]);
    }

    private loadTurnstileScript(): Promise<void> {
        if (ForgotPasswordComponent.turnstileScriptPromise) {
            return ForgotPasswordComponent.turnstileScriptPromise;
        }

        ForgotPasswordComponent.turnstileScriptPromise = new Promise<void>((resolve, reject) => {
            if (this.getTurnstile()) {
                resolve();
                return;
            }

            const rejectLoad = () => {
                document.getElementById("turnstile-script")?.remove();
                reject(new Error("Turnstile failed to load"));
            };

            const existingScript = document.getElementById("turnstile-script");
            if (existingScript) {
                existingScript.addEventListener("load", () => resolve(), { once: true });
                existingScript.addEventListener("error", rejectLoad, { once: true });
                return;
            }

            const script = document.createElement("script");
            script.id = "turnstile-script";
            script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = rejectLoad;
            document.head.appendChild(script);
        }).catch((error) => {
            ForgotPasswordComponent.turnstileScriptPromise = null;
            throw error;
        });

        return ForgotPasswordComponent.turnstileScriptPromise;
    }

    private getTurnstile(): TurnstileApi {
        return (window as Window & { turnstile?: TurnstileApi }).turnstile;
    }

    private resetTurnstile(): void {
        this.captchaToken = null;
        const turnstile = this.getTurnstile();
        if (turnstile && this.widgetId) {
            turnstile.reset(this.widgetId);
        }
    }
}
